import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import {
  getPaginatedRequests,
  getAccountData,
  getPatientEcgCaptures,
  getPatientsData,
  updateRequest,
  getAccountNotificationSettings,
} from './actions';
import {
  AccountResponseDto,
  ECGWaveFormResponseDto,
  PatientResponseDto,
  RequestResponseDto,
  ValueOf,
  RequestResponseWithEmailDto,
  AccountSettingResponseDto,
} from 'types/types';
import { DataStatus, RequestStatus, SortOrder } from 'types/enums';

type State = {
  status: ValueOf<typeof DataStatus>;
  analyzedEcgStatus: ValueOf<typeof DataStatus>;
  analyzedPatientStatus: ValueOf<typeof DataStatus>;
  totalCount: number;
  requests: RequestResponseWithEmailDto[];
  analyzedRequest: null | RequestResponseDto;
  analyzedPatient: null | PatientResponseDto;
  analyzedEcgCaptures: ECGWaveFormResponseDto[];
  analyzedAccount: null | AccountResponseDto;
  analyzedAccountSetting: AccountSettingResponseDto[];
  statusSort: RequestStatus;
  dateSortOrder: SortOrder;
  statusFIlter: RequestStatus | null;
  skip: number;
  limit: number;
};

const initialState: State = {
  requests: [],
  status: DataStatus.IDLE,
  analyzedEcgStatus: DataStatus.IDLE,
  analyzedPatientStatus: DataStatus.IDLE,
  analyzedRequest: null,
  analyzedEcgCaptures: [],
  analyzedAccountSetting: [],
  analyzedPatient: null,
  analyzedAccount: null,
  totalCount: 0,
  statusSort: RequestStatus.PENDING,
  statusFIlter: null,
  dateSortOrder: SortOrder.ASC,
  skip: 0,
  limit: 20,
};

const { reducer, actions } = createSlice({
  initialState,
  name: 'requests',
  reducers: {
    setAnalyzedRequest: (state, { payload }: PayloadAction<null | RequestResponseDto>) => {
      state.analyzedRequest = payload;
    },
    clearAnalyzedData: (state) => {
      state.analyzedRequest = null;
      state.analyzedEcgCaptures = [];
      state.analyzedPatient = null;
      state.analyzedAccount = null;
      state.analyzedEcgStatus = DataStatus.IDLE;
      state.analyzedPatientStatus = DataStatus.IDLE;
    },
    setStatusSort: (state, { payload }: PayloadAction<RequestStatus>) => {
      state.statusSort = payload;
    },
    setDateSortOrder: (state, { payload }: PayloadAction<SortOrder>) => {
      state.dateSortOrder = payload;
    },
    setStatusFilter: (state, { payload }: PayloadAction<RequestStatus | null>) => {
      state.statusFIlter = payload;
    },
    setSkip: (state, { payload }: PayloadAction<number>) => {
      state.skip = payload;
    },
    setLimit: (state, { payload }: PayloadAction<number>) => {
      state.limit = payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(getPaginatedRequests.fulfilled, (state, { payload }) => {
      state.totalCount = payload.totalCount;
      state.requests = payload.requests;
      state.status = DataStatus.SUCCEEDED;
    });
    builder.addCase(getPatientEcgCaptures.fulfilled, (state, { payload }) => {
      state.analyzedEcgCaptures = payload;
      state.analyzedEcgStatus = DataStatus.SUCCEEDED;
    });
    builder.addCase(getPatientEcgCaptures.rejected, (state) => {
      state.analyzedEcgCaptures = [];
      state.analyzedEcgStatus = DataStatus.FAILED;
    });
    builder.addCase(getPatientEcgCaptures.pending, (state) => {
      state.analyzedEcgStatus = DataStatus.LOADING;
    });
    builder.addCase(getAccountData.fulfilled, (state, { payload }) => {
      state.analyzedAccount = payload;
      state.status = DataStatus.SUCCEEDED;
    });
    builder.addCase(getAccountData.rejected, (state) => {
      state.analyzedAccount = null;
    });
    builder.addCase(getPatientsData.fulfilled, (state, { payload }) => {
      state.analyzedPatient = payload;
      state.analyzedPatientStatus = DataStatus.SUCCEEDED;
    });
    builder.addCase(getPatientsData.rejected, (state) => {
      state.analyzedPatient = null;
      state.analyzedPatientStatus = DataStatus.FAILED;
    });
    builder.addCase(getPatientsData.pending, (state) => {
      state.analyzedPatientStatus = DataStatus.LOADING;
    });
    builder.addCase(getAccountNotificationSettings.fulfilled, (state, { payload }) => {
      state.analyzedAccountSetting = payload;
    });
    builder.addMatcher(
      isAnyOf(getPaginatedRequests.pending, getAccountData.pending, updateRequest.pending),
      (state) => {
        state.status = DataStatus.LOADING;
      },
    );
    builder.addMatcher(
      isAnyOf(getPaginatedRequests.rejected, getAccountData.rejected, updateRequest.rejected),
      (state) => {
        state.status = DataStatus.FAILED;
      },
    );
  },
});

export { actions, reducer };
